import React, { useState, useMemo } from "react";
import { Box, Typography, Alert, useMediaQuery, Card, CardContent } from "@mui/material";
import { ResponsiveBar } from "@nivo/bar";
import ChartTooltipPortal from "./shared/ChartTooltipPortal";
import { createBarValueLabelsLayer } from "./shared/BarValueLabelsLayer";
import { useCompanyDividends, isDividendNotFoundError } from "../hooks/useCompanyDividends";
import { useTheme } from "@mui/material/styles";
import dayjs from "dayjs";
import { getChartColors } from "../constants/chartColors";
import {
  ChartHeader,
  GrowthMetricsBar,
  GrowthMetric,
} from "./shared";
import ChartTooltip from "./shared/ChartTooltip";
import { useStockCurrency } from "../../../../contexts/StockCurrencyContext";
import {
  getFlatPortfolioColors,
} from "../../../../utils/getFlatPortfolioColors";

interface DividendPerShareChartProps {
  ticker: string;
  height?: number;
  isModal?: boolean;
  onOpenModal?: () => void;
  onCloseModal?: () => void;
  renderCard?: boolean;
  showFullscreen?: boolean;
}

interface ChartDataPoint {
  period: string;
  dividend: number;
  payments: number;
  paymentDate: string;
  frequency: string;
  [key: string]: string | number; // Required for Nivo BarDatum compatibility
}

type ViewMode = "annual" | "individual";

const VIEW_MODE_OPTIONS = [
  { value: "annual", label: "Annual" },
  { value: "individual", label: "Individual" },
];

const DividendPerShareChart: React.FC<DividendPerShareChartProps> = ({
  ticker,
  height = 400,
  isModal = false,
  onOpenModal,
  onCloseModal,
  renderCard = true,
  showFullscreen = false,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const [modalOpen, setModalOpen] = useState(false);
  const [viewMode, setViewMode] = useState<ViewMode>("individual");
  const { currencySymbol, formatPrice } = useStockCurrency();
  const colors = getFlatPortfolioColors(theme);

  // Fetch dividend history - get enough data for growth calculations
  const { data: dividends, isLoading, error } = useCompanyDividends(ticker, 100);
  const chartColors = getChartColors(theme.palette.mode === "dark");

  const showValueLabels = !isMobile;
  const valueLabelsLayer = useMemo(
    () =>
      createBarValueLabelsLayer({
        yAxisFormat: (value) => formatPrice(value, 2),
        groupMode: "grouped",
        barKeys: ["dividend"],
        textColor: colors.text.primary,
        isModal,
      }),
    [formatPrice, colors.text.primary, isModal]
  );

  const chartData = useMemo((): ChartDataPoint[] => {
    if (!dividends || dividends.length === 0) return [];

    // Filter out special dividends, invalid payment dates, and only include positive amounts
    const regularDividends = dividends.filter(
      (d) => d.frequency !== "Special" && d.dividend > 0 && d.paymentDate && dayjs(d.paymentDate).isValid()
    );

    if (regularDividends.length === 0) return [];

    // Group dividends by calendar year
    const yearlyData: Record<string, { totalDividend: number; payments: number }> = {};

    regularDividends.forEach((d) => {
      const year = dayjs(d.paymentDate).format("YYYY");
      if (!yearlyData[year]) {
        yearlyData[year] = { totalDividend: 0, payments: 0 };
      }
      yearlyData[year].totalDividend += d.dividend;
      yearlyData[year].payments += 1;
    });

    // Show last 10 years, sorted chronologically
    // Exclude current year as it's incomplete data
    const tenYearsAgo = dayjs().subtract(10, "years").format("YYYY");
    const currentYear = dayjs().format("YYYY");

    return Object.entries(yearlyData)
      .filter(([year]) => year >= tenYearsAgo && year !== currentYear)
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([year, data]) => ({
        period: year,
        dividend: data.totalDividend,
        payments: data.payments,
        paymentDate: "",
        frequency: "",
      }));
  }, [dividends]);

  // Individual payments chart data (not aggregated by year)
  const individualChartData = useMemo((): ChartDataPoint[] => {
    if (!dividends || dividends.length === 0) return [];

    // Filter out special dividends, invalid payment dates, and only include positive amounts
    const regularDividends = dividends.filter(
      (d) => d.frequency !== "Special" && d.dividend > 0 && d.paymentDate && dayjs(d.paymentDate).isValid()
    );

    if (regularDividends.length === 0) return [];

    // Show last 10 years of individual payments, sorted chronologically
    const tenYearsAgo = dayjs().subtract(10, "years");

    return regularDividends
      .filter((d) => dayjs(d.paymentDate).isAfter(tenYearsAgo))
      .sort((a, b) => dayjs(a.paymentDate).valueOf() - dayjs(b.paymentDate).valueOf())
      .map((d) => ({
        period: dayjs(d.paymentDate).format("MMM YYYY"),
        dividend: d.dividend,
        payments: 1,
        paymentDate: d.paymentDate,
        frequency: d.frequency,
      }));
  }, [dividends]);

  // Select the appropriate data based on view mode
  const activeChartData = viewMode === "annual" ? chartData : individualChartData;

  // Calculate year-over-year dividend growth metrics
  const growthMetrics = useMemo((): GrowthMetric[] => {
    // chartData already excludes current year
    if (chartData.length < 2) return [];

    const yoyChanges: number[] = [];
    for (let i = 1; i < chartData.length; i++) {
      const prevDividend = chartData[i - 1].dividend;
      const currDividend = chartData[i].dividend;
      if (prevDividend > 0) {
        const change = ((currDividend - prevDividend) / prevDividend) * 100;
        yoyChanges.push(change);
      }
    }

    if (yoyChanges.length === 0) return [];

    const metrics: GrowthMetric[] = [];

    // Most recent YoY change (1Y)
    const lastYoYChange = yoyChanges[yoyChanges.length - 1];
    metrics.push({ label: "YoY Change (1Y)", value: lastYoYChange, stacked: true });

    // 3-year average if enough data
    if (yoyChanges.length >= 3) {
      const last3Years = yoyChanges.slice(-3);
      const avg3Y = last3Years.reduce((a, b) => a + b, 0) / last3Years.length;
      metrics.push({ label: "Avg YoY Change (3Y)", value: avg3Y, stacked: true });
    }

    // 5-year average if enough data
    if (yoyChanges.length >= 5) {
      const last5Years = yoyChanges.slice(-5);
      const avg5Y = last5Years.reduce((a, b) => a + b, 0) / last5Years.length;
      metrics.push({ label: "Avg YoY Change (5Y)", value: avg5Y, stacked: true });
    }

    return metrics;
  }, [chartData]);

  const handleOpenModal = onOpenModal ?? (() => setModalOpen(true));
  const handleCloseModal = onCloseModal ?? (() => setModalOpen(false));

  // Check if company pays dividends
  const hasDividends = activeChartData.length > 0 && activeChartData.some((d) => d.dividend > 0);
  const isEmpty = !activeChartData || activeChartData.length === 0 || !hasDividends;

  // Dynamic title based on view mode
  const chartTitle = viewMode === "annual" ? "Annual Dividend Per Share" : "Dividend History";

  // Wrapper for card rendering with consistent styling
  const wrapInCard = (content: React.ReactNode) => {
    if (!renderCard) return <>{content}</>;
    return (
      <Card
        elevation={0}
        sx={{
          backgroundColor: theme.palette.background.paper,
          borderRadius: 0,
          ...(isModal && { height: "100%", display: "flex", flexDirection: "column" }),
        }}
      >
        <CardContent
          sx={{
            ...(isModal && {
              display: "flex",
              flexDirection: "column",
              height: "100%",
            }),
          }}
        >
          {content}
        </CardContent>
      </Card>
    );
  };

  // Loading state
  if (isLoading) {
    return wrapInCard(
      <>
        <ChartHeader
          title={chartTitle}
          toggleOptions={VIEW_MODE_OPTIONS}
          toggleValue={viewMode}
          onToggleChange={(value) => setViewMode(value as ViewMode)}
          showFullscreen={false}
          isModal={isModal}
          onCloseModal={handleCloseModal}
        />
        <Box
          display="flex"
          justifyContent="center"
          alignItems="center"
          height={height}
        >
          <Typography color="text.secondary">Loading...</Typography>
        </Box>
      </>
    );
  }

  // Special message for non-dividend paying companies (empty data or 404)
  const isNoDividendData = isEmpty || isDividendNotFoundError(error);
  if (!isLoading && isNoDividendData) {
    return wrapInCard(
      <>
        <ChartHeader
          title={chartTitle}
          toggleOptions={VIEW_MODE_OPTIONS}
          toggleValue={viewMode}
          onToggleChange={(value) => setViewMode(value as ViewMode)}
          showFullscreen={false}
          isModal={isModal}
          onCloseModal={handleCloseModal}
        />
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            py: 4,
            px: 2,
          }}
        >
          <Typography
            sx={{
              color: colors.text.secondary,
              fontSize: "0.875rem",
              textAlign: "center",
            }}
          >
            This company does not currently pay dividends, or dividend data could not be loaded.
          </Typography>
        </Box>
      </>
    );
  }

  // Error state (non-404 errors)
  if (error) {
    return wrapInCard(
      <>
        <ChartHeader
          title={chartTitle}
          toggleOptions={VIEW_MODE_OPTIONS}
          toggleValue={viewMode}
          onToggleChange={(value) => setViewMode(value as ViewMode)}
          showFullscreen={false}
          isModal={isModal}
          onCloseModal={handleCloseModal}
        />
        <Alert severity="error" sx={{ mt: 1 }}>
          Unable to load dividend data.
        </Alert>
      </>
    );
  }

  const chartContent = (
    <>
      <ChartHeader
        title={chartTitle}
        toggleOptions={VIEW_MODE_OPTIONS}
        toggleValue={viewMode}
        onToggleChange={(value) => setViewMode(value as ViewMode)}
        showFullscreen={showFullscreen}
        isModal={isModal}
        onOpenModal={handleOpenModal}
        onCloseModal={handleCloseModal}
      />

      <Box
        sx={{
          height: height,
          ...(isModal && { flex: 1, minHeight: 0 }),
        }}
      >
        <ResponsiveBar
          data={activeChartData}
          keys={["dividend"]}
          indexBy="period"
          margin={{
            top: isMobile ? 10 : 30,
            right: isMobile ? 10 : 20,
            bottom: (() => {
              // Individual view needs more space for longer rotated labels (e.g., "Jun 2016")
              if (viewMode === "individual") return isMobile ? 55 : 65;
              // Annual view with growth metrics
              if (growthMetrics.length > 0) return isMobile ? 50 : 60;
              // Annual view without growth metrics
              return isMobile ? 35 : 40;
            })(),
            left: isMobile ? 70 : 90,
          }}
          padding={viewMode === "individual" ? 0.15 : 0.3}
          valueScale={{ type: "linear", min: 0, max: "auto" }}
          indexScale={{ type: "band", round: true }}
          colors={chartColors.positive}
          axisTop={null}
          axisRight={null}
          axisBottom={{
            tickSize: 0,
            tickPadding: isMobile ? 10 : 8,
            tickRotation: -45,
            tickValues: (() => {
              // Show fewer labels on mobile, and space them out for readability
              const maxLabels = isMobile ? 5 : Math.min(activeChartData.length, 12);
              if (activeChartData.length <= maxLabels) return undefined;
              return activeChartData
                .filter((_, i) => i % Math.ceil(activeChartData.length / maxLabels) === 0)
                .map((d) => d.period);
            })(),
          }}
          axisLeft={{
            tickSize: 0,
            tickPadding: isMobile ? 10 : 8,
            tickRotation: 0,
            legend: isMobile ? undefined : (viewMode === "annual" ? `Annual DPS (${currencySymbol})` : `DPS (${currencySymbol})`),
            legendPosition: "middle",
            legendOffset: -70,
            format: (value) => formatPrice(value as number, 2),
            tickValues: isMobile ? 4 : 5,
          }}
          enableLabel={false}
          enableGridY={true}
          gridYValues={isMobile ? 4 : 5}
          layers={[
            "grid",
            "axes",
            "bars",
            ...(showValueLabels ? [valueLabelsLayer] : []),
            "markers",
            "legends",
            "annotations",
          ]}
          theme={{
            background: "transparent",
            text: {
              fontSize: isMobile ? 10 : 12,
              fill: colors.text.primary,
            },
            axis: {
              domain: { line: { stroke: "transparent", strokeWidth: 0 } },
              legend: {
                text: {
                  fontSize: isMobile ? 10 : 12,
                  fill: colors.text.primary,
                  fontWeight: 600,
                },
              },
              ticks: {
                line: { stroke: "transparent", strokeWidth: 0 },
                text: {
                  fontSize: isMobile ? 10 : 11,
                  fill: colors.text.secondary,
                  fontWeight: 600,
                },
              },
            },
            grid: {
              line: {
                stroke: colors.border.primary,
                strokeWidth: 1,
                strokeOpacity: 0.3,
              },
            },
            tooltip: {
              container: {
                background: colors.background.secondary,
                color: colors.text.primary,
                fontSize: 12,
                borderRadius: 0,
                boxShadow: "none",
                border: `1px solid ${colors.border.primary}`,
              },
            },
          }}
          tooltip={({ indexValue, value, data, color }) => {
            const chartPoint = data as ChartDataPoint;
            const header = viewMode === "individual" && chartPoint.paymentDate
              ? dayjs(chartPoint.paymentDate).format("MMMM D, YYYY")
              : String(indexValue);
            const rows = [
              {
                label: viewMode === "annual" ? "Annual Dividend" : "Dividend",
                value: `${formatPrice(Math.floor((value as number) * 100) / 100, 2)} per share`,
              },
              ...(viewMode === "annual"
                ? [{ label: "Payments", value: `${chartPoint.payments} payment${chartPoint.payments !== 1 ? "s" : ""}` }]
                : []),
              ...(viewMode === "individual" && chartPoint.frequency
                ? [{ label: "Frequency", value: chartPoint.frequency }]
                : []),
            ];
            return (
              <ChartTooltipPortal>
                <ChartTooltip header={header} headerColor={color} rows={rows} />
              </ChartTooltipPortal>
            );
          }}
        />
      </Box>

      <GrowthMetricsBar metrics={growthMetrics} />
    </>
  );

  const modalContent = (
    <DividendPerShareChart
      ticker={ticker}
      height={700}
      isModal={true}
      renderCard={false}
      onCloseModal={handleCloseModal}
    />
  );

  return (
    <>
      {wrapInCard(chartContent)}
      {/* Modal for fullscreen view */}
      {modalOpen && (
        <Box
          onClick={handleCloseModal}
          sx={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: "rgba(0, 0, 0, 0.8)",
            zIndex: 1300,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            p: 2,
          }}
        >
          <Box
            onClick={(e) => e.stopPropagation()}
            sx={{
              width: "90vw",
              height: "90vh",
              backgroundColor: theme.palette.background.paper,
              borderRadius: 4,
              display: "flex",
              flexDirection: "column",
              p: 2,
            }}
          >
            {modalContent}
          </Box>
        </Box>
      )}
    </>
  );
};

export default DividendPerShareChart;
